import { format } from 'date-fns'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
} from 'types/assetModel/assetModelEnums'
import {
  assetModelFormFields,
  GeckoAssetModel,
} from 'types/entries/geckoAssetModel'
import {
  FormField,
  getFormFieldByDataKey,
} from 'types/geckoFormTypesAndHelpers'
import { EntryPutJson } from 'types/helpers'
import {
  DomeAssetModelJSON,
  DomeWallAssetModelJSON,
  FlatCircularFloorAssetModelJSON,
  RolodexEntryJson,
} from 'types/index'
import { parseDate, parseNumber } from 'utils/parsingHelpers'

import { BaseAssetModelData } from './baseAssetModelData'

export type GeckoNuclearContainmentDomeAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.NuclearContainmentDome
  installation_date: string | undefined
  components: Record<
    string,
    | DomeAssetModelJSON
    | DomeWallAssetModelJSON
    | FlatCircularFloorAssetModelJSON
  >
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
}

export const NuclearContainmentDomeAvailableComponents: GeckoComponentModelType[] =
  [
    GeckoComponentModelType.Dome,
    GeckoComponentModelType.DomeWall,
    GeckoComponentModelType.FlatCircularFloor,
  ]

export const NewNuclearContainmentDomeAssetModelJSON: GeckoNuclearContainmentDomeAssetModelJSON =
  {
    name: 'New Asset Model Nuclear Containment Dome',
    type: GeckoAssetModelType.NuclearContainmentDome,
    installation_date: undefined,
    components: {},
    effective_date: format(Date.now(), 'yyyy-MM-dd'),
    compass_orientation: undefined,
    plant_north_compass_orientation: undefined,
  }

export class GeckoNuclearContainmentDomeAssetModel extends GeckoAssetModel<BaseAssetModelData> {
  private constructor(json: RolodexEntryJson) {
    super(json)
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoNuclearContainmentDomeAssetModel {
    return new GeckoNuclearContainmentDomeAssetModel(json)
  }

  public asJSON(): GeckoNuclearContainmentDomeAssetModelJSON {
    return generateNuclearContainmentDomeAssetModel(
      this.data.name,
      this.data.installation_date,
      this.raw.data.components as Record<
        string,
        | DomeAssetModelJSON
        | DomeWallAssetModelJSON
        | FlatCircularFloorAssetModelJSON
      >,
      this.data.effective_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation
    )
  }
}

export function generateNuclearContainmentDomeAssetModel(
  name: string,
  installation_date: Date | undefined,
  components: Record<
    string,
    | DomeAssetModelJSON
    | DomeWallAssetModelJSON
    | FlatCircularFloorAssetModelJSON
  >,
  effective_date: Date,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined
): GeckoNuclearContainmentDomeAssetModelJSON {
  const domeInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  const domeEffectiveDate = effective_date.toISOString().split('T')[0]
  return {
    name: name,
    type: GeckoAssetModelType.NuclearContainmentDome,
    installation_date: domeInstallationDate,
    components: components,
    effective_date: domeEffectiveDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
  }
}

export const pipingModelFormFields = (
  assetModelJSON: GeckoNuclearContainmentDomeAssetModelJSON
) => {
  const assetModelfields = assetModelFormFields(assetModelJSON)

  return [...assetModelfields]
}

export const DomeJSONFromFormFields = (
  assetModelJSON: GeckoNuclearContainmentDomeAssetModelJSON,
  formFields: FormField[]
) => {
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generateNuclearContainmentDomeAssetModel(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    parseNumber(
      getFormFieldByDataKey(formFields, 'compass_orientation')?.value
    ),
    parseNumber(
      getFormFieldByDataKey(formFields, 'plant_north_compass_orientation')
        ?.value
    )
  )
}

export function regenerateNuclearContainmentDomeAssetModel(
  assetModel: GeckoAssetModel
): EntryPutJson {
  const rawAssetModel = assetModel.raw
  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }
  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.Dome:
        regeneratedComponents[componentID] = regenerateDomeComponent(component)
        break
      case GeckoComponentModelType.DomeWall:
        regeneratedComponents[componentID] =
          regenerateDomeWallComponent(component)
        break
      case GeckoComponentModelType.FlatCircularFloor:
        regeneratedComponents[componentID] =
          regenerateFlatCircularFloorComponent(component)
        break
      default:
        throw new Error(
          'Unable to determine this type of Nuclear Containment Dome Component!'
        )
    }
  }
  assetModel.raw.data.components = regeneratedComponents
  return assetModel.raw
}

export function regenerateDomeComponent(
  dome: Record<string, unknown>
): Record<string, unknown> {
  return dome
}

export function regenerateDomeWallComponent(
  domeWall: Record<string, unknown>
): Record<string, unknown> {
  return domeWall
}

export function regenerateFlatCircularFloorComponent(
  flatCircularFloor: Record<string, unknown>
): Record<string, unknown> {
  return flatCircularFloor
}
